/* eslint-disable max-len */

import { isNumber } from 'lodash';
import { MediaFile, Property, PropertyPatch, SurveyPatch } from '../types';
import { monthNames } from '../map/constants/MapStyles';
/**
 * get chunks of array list.
 * @param originArray
 * @param size
 * @param chunkIndex
 */
export const getArrayChunks = (originArray: any, size: number) => Array.from(
  new Array(Math.ceil(originArray.length / size)),
  (_, i) => originArray.slice(i * size, i * size + size),
);
  /**
   * Determines whether the given time is in the AM or PM period.
   * @param newMessage - If true, uses the current time; if false, uses the provided date parameter.
   * @param date - Optional date object to extract the hour from. Used when newMessage is false.
   * @returns A string indicating the period: 'am' for times before 12:00, 'pm' for times 12:00 and after.
   */
const dayTimes = (newMessage: boolean, date?: any) => {
  const currentTime = new Date();
  const currentHours = newMessage ? currentTime.getHours() : date.getHours();
  let dayTime = 'am';
  if (currentHours >= 12) {
    dayTime = 'pm';
  }
  return dayTime;
};
/**
 * return the formatted date.
 * @param date
 * @returns
 */
const getCurrentHours = (date: any) => {
  const standardHourTime = date.getHours();
  if (date.getHours() > 12) {
    return standardHourTime - 12;
  }
  return standardHourTime;
};
/**
 * return the formatted date.
 * @param date
 * @returns
 */
export const getFormattedDate = (date: Date, includeTimeStamp: boolean) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const monthIndex = date.getMonth();
  const monthName = monthNames[monthIndex];
  const year = date.getFullYear();
  const day = date.getUTCDate();
  return includeTimeStamp ?
    `${monthName} ${day}, ${year} @ ${getCurrentHours(date)}:${getCurrentMinutes(date, true)} ${dayTimes(true)}`
    : `${monthName} ${day},  ${year}`;
};

export const checkValueIsUnavailable = (obj: any) =>
  obj === null || obj === undefined || obj === '';

// eslint-disable-next-line arrow-body-style
export const checkCoordinateIsvalid = (obj: any) => {
  return obj === null || obj === undefined || obj === '' || !isNumber(Number(obj));
};

/**
 * Constructs a formatted full address string for the Netherlands.
 *
 * @param street - The street address.
 * @param zip - The postal code.
 * @param city - The city name.
 * @param state - The state or province name.
 * @returns A formatted address string with each part separated by a newline.
 *          Returns an empty string if all input parameters are empty.
 *
 * The address is formatted as:
 * - Line 1: Street
 * - Line 2: Combination of zip, city, and state, separated appropriately.
 */
export const getFullAddressStr_CE_NetharLands = (
  street: string,
  city: string,
  state: string,
  zip: string,
) => {
  if (!street && !zip && !city && !state) return '';
  const line1 = zip ? `${street}, ${zip}, ` : street;
  let line2 = '';
  if (city) line2 += city;
  if (city && state) line2 += ', ';
  if (state) line2 += state;

  const fullAddressValidParts = [line1, line2].filter((p) => p);
  return fullAddressValidParts.join('\n');
};

/**
 * Returns full address formatted
 * @param street
 * @param city
 * @param state
 * @param zip
 * @returns
 */
export const getFullAddressStr = (
  street: string,
  city: string,
  state: string,
  zip: string,
) => {
  if (!street && !city && !state && !zip) return '';
  const line1 = street;
  let line2 = '';
  if (city) line2 += city;
  if (city && state) line2 += ', ';
  if (city && !state && zip) line2 += ' ';
  if (state) line2 += state;
  if ((city && zip) || (state && zip)) line2 += ' ';
  if (zip) line2 += zip;

  const fullAddressValidParts = [line1, line2].filter((p) => p);
  return fullAddressValidParts.join('\n');
};

/**
 * Constructs a full address string for the UK based on the provided street, zip, and city.
 *
 * @param street - The street address. Defaults to an empty string if not provided.
 * @param zip - The postal code. Defaults to an empty string if not provided.
 * @param city - The city name. Defaults to an empty string if not provided.
 * @returns A formatted address string combining the street, city, and zip.
 *          Returns an empty string if all fields are empty.
 */
export const getFullAddressStr_UK = (
  street: string = '',
  city: string = '',
  zip: string = '',
): string => {
  const isAllFieldsEmpty = !street && !zip && !city;
  if (isAllFieldsEmpty) return '';

  const line1 = street ? `${street}, ${city}`.trim() : city.trim();
  const line2 = zip.trim();

  return [line1, line2].filter(Boolean).join(' ');
};

/**
 * check if the object is empty after converted from string
 * @param inputStr
 * @returns
 */
export const isEmptyObjectFromStr = (inputStr: string) =>
  Object.keys(JSON.parse(inputStr)).length === 0;

export const mediaFileToGalleryFile = (media: MediaFile) => ({
  id: media.mediaId,
  url: media.origin !== 'SiteIQ' ? `${media.cloudfrontUrl}` : media.cloudfrontUrl,
  previewUrl: media.pdfImageUrl,
  thumbnailUrl: media.thumbnailUrl,
  displayText: media.displayName,
  active: !media.isHidden,
  primary: false,
  order: media.order,
  deletable: (!(media.origin && media.origin.toLowerCase() === 'siteiq')),
  origin: media.origin,
  modifiedFromMediaId: media.modifiedFromMediaId,

});

export const isChangingMapSetting = (
  patch: SurveyPatch | PropertyPatch,
) => {
  const attributes = [
    'isIncludeLegend',
    'isIncludeHeader',
    'pdfPaperSize',
  ];
  if (Object.keys(patch).some((attr) => attributes.includes(attr))) {
    return true;
  }
  return false;
};

export const flattenMediaFilesWithModifiedFiles = (flyers: any[]) => {
  const files = flyers;
  files.forEach((item, i) => {
    const index = files.findIndex((res) => item.mediaId === res.modifiedFromMediaId);
    if (index > -1) {
      files[i] = files[index];
      files.splice(index, 1);
    }
  });
  return files;
};
export const surveyShortlistedOverride = (
  shortlisted: boolean,
  property: Property,
) =>
  (shortlisted && property.metaData?.isClientShortlist && !property.isHidden) ||
  (!property.isHidden && !shortlisted);

export const addElementIdToMUINestedElement = (element: any, id: string) => element?.setAttribute('id', id);

export { default as renderLinkFieldValue } from './renderLinkFieldValue';

/**
 * return the formatted date.
 * @param date
 * @returns
 */
const getCurrentMinutes = (date: any, isNewMessage: boolean) => {
  // const currentMinutes = new Date().getMinutes();
  // const numberOfDigits = currentMinutes.toString();
  // if (date.getMinutes().toString().length <= 1 || numberOfDigits.length <= 1) {
  if (date.getMinutes().toString().length <= 1) {
    const addZeroBeginingOfDigit = `0${date.getMinutes().toString()}`;
    // currentMinutes = Number(addZeroBeginingOfDigit);
    // return isNewMessage ? `0${numberOfDigits}` : `0${date.getMinutes().toString()}`;
    // return `0${numberOfDigits}`;
    return addZeroBeginingOfDigit;
  }
  return date.getMinutes().toString();
};
/**
 * return the formatted date.
 * @param date
 * @returns
 */
const convertUTCDateToLocalDate = (date: any) => {
  const newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
  const offset = date.getTimezoneOffset() / 60;
  const hours = date.getHours();
  newDate.setHours(hours - offset);
  return newDate;
};
/**
 * Formats a date string into a readable comment date format with local time.
 * 
 * @param dateData - A date string in ISO format (YYYY-MM-DD) or Date object to format
 * @returns A formatted date string in the format "Month Day, Year @ HH:MM AM/PM" 
 *          or a default formatted date if the input is invalid
 * 
 * @example
 * // Returns "January 15, 2024 @ 02:30 PM"
 * getCommentsDate('2024-01-15T14:30:00Z')
 * 
 * @remarks
 * - Converts UTC dates to local time
 * - Returns month name in full format (e.g., "January")
 * - Includes 12-hour time format with AM/PM indicator
 * - Falls back to current date if input is invalid or incomplete
 */
/**
 * Parse input into a Date object (UTC expected) or return null if invalid.
 */
const parseToDate = (input: any): Date | null => {
  if (!input) return null;
  if (input instanceof Date) return input;
  if (typeof input === 'number') return new Date(input);
  if (typeof input === 'string') {
    // Prefer built-in parsing for ISO formats
    const parsed = Date.parse(input);
    if (!Number.isNaN(parsed)) return new Date(parsed);
    // Try YYYY-MM-DD or similar fallbacks
    const parts = input.split('-').map((p) => p && p.trim());
    if (parts.length >= 3) {
      const year = Number(parts[0]);
      const month = Number(parts[1]) - 1; // JS months 0..11
      const day = Number(parts[2].slice(0, 2));
      if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)) {
        return new Date(Date.UTC(year, month, day));
      }
    }
  }
  return null;
};

/**
 * Return a comment-style formatted date string. If input is invalid, fall back to the
 * current formatted date.
 */
export const getCommentsDate = (dateData: any): string => {
  const parsed = parseToDate(dateData);
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return getFormattedDate(new Date(), true);
  }

  // Convert UTC -> local time for display
  const local = convertUTCDateToLocalDate(parsed);
  const monthName = monthNames[local.getMonth()];
  const day = local.getDate();
  const year = local.getFullYear();
  const hours = getCurrentHours(local);
  const minutes = getCurrentMinutes(local, false);
  const ampm = dayTimes(false, local).toUpperCase();

  return `${monthName} ${day}, ${year} @ ${hours}:${minutes} ${ampm}`;
};

/** Maps MIQ locale code → i18n language key supported by the app */
export const LOCALE_TO_I18N: Record<string, string> = {
  en: 'en', en_GB: 'en',
  de_DE: 'de', de_AT: 'de',
  nl: 'nl', pt: 'pt', no: 'no',
  es: 'es', es_ES: 'es',
  zh: 'zh', ko: 'ko',
};

/** Returns the i18n key for a given MIQ locale code, defaulting to 'en' */
export const localeToI18nCode = (localeCode?: string): string =>
  (localeCode && LOCALE_TO_I18N[localeCode]) || 'en';

/**
 * Returns the column header label.
 * For English, always uses the API-supplied label directly.
 * For other languages, falls back to the translation key, then the API label.
 */
export const getColumnHeader = (
  t: (key: string, options?: object) => string,
  language: string,
  key: string,
  apiLabel: string,
): string => {
  if (language.startsWith('en')) return apiLabel;
  return t(`columnHeaders.${key}`, { defaultValue: apiLabel });
};
